/**
 * How strong a password is — the estimator behind <wdc-password-meter>.
 *
 * The API runs the same estimator (\system\auth\PasswordStrength) on every
 * password change and refuses what this would, and bin/tools/password-selftest
 * holds the two to one set of answers, so the meter never calls a password
 * strong that the server then turns down. Change the rules in BOTH, and in
 * the vectors file.
 *
 * Deliberately simple enough to explain on screen:
 *   refused  shorter than the minimum; one of the ~19,400 most common
 *            passwords; or containing the person's username or name
 *   bits     a common word with digits or symbols bolted on counts as one
 *            guess from the common list plus those characters; anything
 *            else is length x the variety of characters used, where runs
 *            ("aaaa") and sequences ("1234", "abcd") count once
 *   levels   weak / fair / good / strong by the thresholds in the policy file
 *
 * No character type is required: length and variety trade off, so a short
 * password needs a mix to be strong and a long one does not.
 */
export const LEVELS = ['refused', 'weak', 'fair', 'good', 'strong'];

const POLICY_URL = '/core/data/password-policy.json';
const LIST_URL = '/core/data/common-passwords.txt';

// Look-alike substitutions undone before a common-list lookup (p@ssw0rd).
const UNLEET = { 0: 'o', 1: 'i', 3: 'e', 4: 'a', 5: 's', 7: 't', '@': 'a', $: 's', '!': 'i' };

let loading = null;

/** The policy and the common list, fetched once per page. */
export function loadRules() {
  loading ??= (async () => {
    try {
      const [policy, list] = await Promise.all([fetch(POLICY_URL), fetch(LIST_URL)]);
      if (!policy.ok || !list.ok) throw new Error('Could not load the password rules');
      return { policy: await policy.json(), common: parseList(await list.text()) };
    } catch (err) {
      loading = null;          // let the next call try again
      throw err;
    }
  })();
  return loading;
}

export function parseList(text) {
  return new Set(text.split('\n').map(l => l.trim()).filter(Boolean));
}

/**
 * @param {string} password
 * @param {string[]} context  words it must not contain: username, first and last name
 * @param {{policy: object, common: Set<string>}} rules
 * @returns {{level: number, label: string, bits: number, reason: string|null}}
 *   reason: 'short' | 'common' | 'name' | 'common-word' | 'weak' | null (strong)
 */
export function assess(password, context, { policy, common }) {
  const chars = [...password];
  const lower = password.toLowerCase();
  const refused = (reason) => ({ level: 0, label: LEVELS[0], bits: 0, reason });

  if (chars.length < policy.min_length) return refused('short');
  if (common.has(lower) || common.has(unleet(lower))) return refused('common');
  for (const word of context) {
    const w = String(word ?? '').toLowerCase();
    if ([...w].length >= 4 && lower.includes(w)) return refused('name');
  }

  const { bits, commonWord } = estimate(password, common);
  const t = policy.bits;
  const level = bits >= t.strong ? 4 : bits >= t.good ? 3 : bits >= t.fair ? 2 : 1;
  const reason = level === 4 ? null : (commonWord ? 'common-word' : 'weak');
  return { level, label: LEVELS[level], bits, reason };
}

/** The level a password must reach to be saved. */
export function requiredLevel(policy) {
  return LEVELS.indexOf(policy.required);
}

function estimate(password, common) {
  // A word at the core, dressed with digits or symbols at either end.
  const [, head, core, tail] = password.match(/^([^\p{L}]*)(.*?)([^\p{L}]*)$/su);
  if ([...core].length >= 4) {
    const word = core.toLowerCase();
    if (common.has(word) || common.has(unleet(word))) {
      let bits = Math.log2(common.size) + (core !== word ? 1 : 0);
      for (const c of head + tail) bits += Math.log2(poolOf(c));
      return { bits: round(bits), commonWord: true };
    }
  }

  let pool = 0;
  if (/[a-z]/.test(password)) pool += 26;
  if (/[A-Z]/.test(password)) pool += 26;
  if (/[0-9]/.test(password)) pool += 10;
  if (/[ -/:-@[-`{-~]/.test(password)) pool += 33;
  if (/[^ -~]/.test(password)) pool += 100;
  return { bits: round(effectiveLength(password) * Math.log2(pool)), commonWord: false };
}

// The variety one character stands for: its own kind of character.
function poolOf(c) {
  if (/[0-9]/.test(c)) return 10;
  if (/[a-zA-Z]/.test(c)) return 26;
  if (/[ -~]/.test(c)) return 33;
  return 100;
}

// Characters that add a guess: a repeat of the one before, or the next step
// of a +1/-1 run ("abc", "321"), adds nothing.
function effectiveLength(password) {
  const cps = [...password].map(c => c.codePointAt(0));
  let n = 0;
  cps.forEach((c, i) => {
    if (i >= 1 && c === cps[i - 1]) return;
    if (i >= 2 && Math.abs(c - cps[i - 1]) === 1 && c - cps[i - 1] === cps[i - 1] - cps[i - 2]) return;
    n += 1;
  });
  return n;
}

function unleet(s) {
  return [...s].map(c => UNLEET[c] ?? c).join('');
}

function round(bits) {
  return Math.round(bits * 100) / 100;
}
